import './FlipCard.scss'

import { useIsWindowBiggerThan } from '@bob/library'
import React, {
  FC,
  ReactNode,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import AnimateHeight from 'react-animate-height'

import IconProvider from '../../../coral-library/icons/IconProvider'

type FlipCardContent = {
  frontHeader: ReactNode
  frontContent: ReactNode
  backHeader: ReactNode
  backContent: ReactNode
}

type FlipCardProps = {
  backgroundColor?: string
  buttonColor?: string
  content: FlipCardContent
  className?: string
  cardClassName?: string
  headerBottomPadding?: boolean
  unhoverOnClick?: boolean
  animateHeight?: boolean
  animateFixedMobile?: SetStateAction<number | 'auto'>
  animateFixedTablet?: SetStateAction<number | 'auto'>
  onHoverChange?: (isHovered: boolean) => void
  onFlipStartChange?: (isMidpoint: boolean) => void
  onFlipEndChange?: (delayDone: boolean, isHovered: boolean) => void
  onFlipChange?: (isFrontSide: boolean) => void
}

// eslint-disable-next-line max-lines-per-function
export const FlipCard: FC<FlipCardProps> = ({
  backgroundColor,
  buttonColor,
  content,
  className,
  cardClassName,
  headerBottomPadding,
  unhoverOnClick,
  animateHeight,
  animateFixedMobile = 'auto',
  animateFixedTablet = 'auto',
  onHoverChange,
  onFlipStartChange,
  onFlipEndChange,
  onFlipChange,
}) => {
  const [headerHeight, setHeaderHeight] = useState<number>(0)
  const [contentHeight, setContentHeight] = useState<number>(0)
  const [isFlipping, setIsFlipping] = useState(false)
  const [isFrontView, setIsFrontView] = useState(true)
  const [frontViewState, setFrontViewState] = useState('visible')
  const [backViewState, setBackViewState] = useState('invisible')
  const [height, setHeight] = useState<number | 'auto'>(400)

  const animationHideDuration = 400
  const isTablet = useIsWindowBiggerThan(719)
  const isDesktop = useIsWindowBiggerThan(1079)

  const headlineRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const iconSize: number = useMemo(() => (isDesktop ? 40 : 30), [isDesktop])
  const cardRef = useRef<HTMLDivElement>(null)
  const isHoveringRef = useRef(false)

  const handleMouseEnter = () => {
    isHoveringRef.current = true
    if (isFrontView) onHoverChange?.(true)
  }

  const handleMouseLeave = () => {
    isHoveringRef.current = false
    onHoverChange?.(false)
  }

  useEffect(() => {
    if (!isFrontView) setHeight('auto')
    else {
      if (isTablet) setHeight(animateFixedTablet)
      else setHeight(animateFixedMobile)
    }
  }, [isTablet, isFrontView, animationHideDuration, animateFixedTablet, animateFixedMobile])

  const handleFlip = () => {
    setIsFlipping(true)
    setIsFrontView((prev) => !prev)
    if (unhoverOnClick) onHoverChange?.(false)
    if (frontViewState === 'visible') {
      setFrontViewState('hiding')
      setHeight('auto')
    } else {
      setBackViewState('hiding')
      if (isTablet) setHeight(612)
      else setHeight(400)
    }
    requestAnimationFrame(() => {
      onFlipStartChange?.(false)
      setTimeout(() => {
        if (isFrontView) {
          setBackViewState('visible')
          setFrontViewState('invisible')
        } else {
          setFrontViewState('visible')
          setBackViewState('invisible')
        }
        setIsFlipping(false)
        onFlipStartChange?.(true)
        onFlipEndChange?.(false, false)
        setTimeout(() => {
          setTimeout(() => {
            onFlipEndChange?.(true, isHoveringRef.current)
            onFlipChange?.(isFrontView)
          }, 400)
        }, 400)
      }, animationHideDuration)
    })
  }

  useLayoutEffect(() => {
    if (headlineRef.current) setHeaderHeight(headlineRef.current.offsetHeight)
    if (contentRef.current) setContentHeight(contentRef.current.offsetHeight)
  }, [isTablet, isFrontView])
  useEffect(() => {
    const headlineEl = headlineRef.current
    const contentEl = contentRef.current

    if (!headlineEl || !contentEl) return

    const handleResize = () => {
      setHeaderHeight(headlineEl.offsetHeight)
      setContentHeight(contentEl.offsetHeight)
    }

    const resizeObserver = new ResizeObserver(() => {
      handleResize()
    })

    resizeObserver.observe(headlineEl)
    resizeObserver.observe(contentEl)
    handleResize()

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <AnimateHeight
      className={`coral-flip-card-height ${cardClassName || ''}`}
      duration={animateHeight ? animationHideDuration : 0}
      height={animateHeight ? height : 'auto'}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={`coral-flip-card m--${backgroundColor}  ${className || ''}`}
        onClick={handleFlip}
        tabIndex={0}
      >
        <div
          className={`coral-flip-card__card-container m--${frontViewState}  ${className || ''} ${
            isFrontView ? 'front-view' : ''
          }`}
          ref={cardRef}
          style={isTablet ? undefined : { height: `${contentHeight + headerHeight}px` }}
        >
          <div
            className={`coral-flip-card__headline-container m--padding-${headerBottomPadding}`}
            ref={headlineRef}
          >
            <div
              className={`coral-flip-card__front-headline m--${frontViewState}`}
              style={
                frontViewState === 'invisible' || frontViewState === 'hiding'
                  ? { transform: `translateY(${headerHeight}px)` }
                  : undefined
              }
            >
              {content.frontHeader}
            </div>

            <div
              className={`coral-flip-card__back-headline m--${backViewState}`}
              style={
                backViewState === 'invisible' || backViewState === 'hiding'
                  ? { transform: `translateY(${headerHeight}px)` }
                  : undefined
              }
            >
              {content.backHeader}
            </div>
            <div
              className={
                `coral-flip-card__flip-button m--${buttonColor}` +
                (!isFrontView ? ' flipped' : '') +
                (isFlipping ? ' clicked' : '')
              }
            >
              <IconProvider name={'boldPlus'} size={iconSize} />
            </div>
          </div>

          <div className={'coral-flip-card__content-container'}>
            <div className={'coral-flip-card__content-stack'}>
              <div className={`coral-flip-card__front-content m--${frontViewState}`}>
                <div
                  className={`coral-flip-card__front-image-container m--${frontViewState}`}
                  style={
                    frontViewState === 'invisible' || frontViewState === 'hiding'
                      ? { transform: `translateY(${contentHeight + headerHeight + 50}px)` }
                      : undefined
                  }
                >
                  {content.frontContent}
                </div>
              </div>

              <div className={`coral-flip-card__back-content m--${backViewState}`} ref={contentRef}>
                <div
                  className={`coral-flip-card__back-section `}
                  style={
                    backViewState === 'invisible' || backViewState === 'hiding'
                      ? { transform: `translateY(${contentHeight + headerHeight + 50}px)` }
                      : undefined
                  }
                >
                  {content.backContent}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AnimateHeight>
  )
}
